import { memo, useMemo, useCallback, useState, useEffect, useRef } from 'react';
import {
    Box, Flex, Heading, Text, Button, SimpleGrid,
    Image, Skeleton, SkeletonText, SkeletonCircle, Badge,
    Modal, ModalOverlay, ModalContent, ModalBody,
    Input, InputGroup, InputLeftElement, IconButton,
    useDisclosure,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useDebounce from 'hooks/useDebounce';
import { useVendors, normalizeVendor, buildVendorUrl, isDisplayableVendor } from 'hooks/useVendors';
import ProductItemNew from 'components/Dashboard/ProductItemNew';
import Brands from 'components/Dashboard/Brands';
import useGetUserDetails from 'hooks/useGetUserDetail';
import { useGetProducts } from 'hooks/useProducts';

/* ─────────────────────────────────────────────────────────────
   BRAND TOKENS
   ───────────────────────────────────────────────────────────── */
const NAVY        = '#1B3B5F';
const NAVY_DEEP   = '#0F2940';
const CYAN        = '#DCF8F9';
const INK         = '#1A2740';
const MUTED       = '#5A6B7E';
const FAINT       = '#8896AE';
const SURFACE     = '#FFFFFF';
const SURFACE_ALT = '#F7F9FB';
const BORDER      = '#E8EDF5';

const SHADOW_SM = '0 1px 3px rgba(27,59,95,0.06)';

const FONT = "'Bricolage Grotesque', sans-serif";

/* ─────────────────────────────────────────────────────────────
   ICONS
   ───────────────────────────────────────────────────────────── */
const SparkleIcon = (props) => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path d="M12 2L13.5 9H20L14.5 13.5L16.5 21L12 17L7.5 21L9.5 13.5L4 9H10.5L12 2Z" />
    </svg>
);
const TagIcon = (props) => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path d="M21.41 11.58L12.41 2.58C12.04 2.21 11.53 2 11 2H4C2.9 2 2 2.9 2 4V11C2 11.53 2.21 12.04 2.59 12.42L11.59 21.42C11.95 21.79 12.44 22 13 22C13.56 22 14.05 21.79 14.41 21.41L21.41 14.41C21.79 14.05 22 13.56 22 13C22 12.44 21.78 11.95 21.41 11.58ZM6.5 7.5C5.67 7.5 5 6.83 5 6C5 5.17 5.67 4.5 6.5 4.5C7.33 4.5 8 5.17 8 6C8 6.83 7.33 7.5 6.5 7.5Z" />
    </svg>
);
const TrophyIcon = (props) => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path d="M19 5H17V3H7V5H5C3.9 5 3 5.9 3 7V8C3 10.55 4.92 12.63 7.39 12.94C8.02 14.44 9.37 15.57 11 15.9V18H8V20H16V18H13V15.9C14.63 15.57 15.98 14.44 16.61 12.94C19.08 12.63 21 10.55 21 8V7C21 5.9 20.1 5 19 5Z" />
    </svg>
);
const ClockIcon = (props) => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM12.5 7H11V13L16.25 16.15L17 14.92L12.5 12.25V7Z" />
    </svg>
);
const ChevronRightIcon = (props) => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const StoreIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M3 9l1.5-5h15L21 9 M3 9v11h18V9 M3 9h18 M8 14h8" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
);
const SearchIcon = (props) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" {...props}>
        <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
        <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);
const CloseIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);
const ArrowRightIcon = (props) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const InboxIcon = (props) => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M22 12h-6l-2 3h-4l-2-3H2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5.5 5h13l3 7v6a2 2 0 01-2 2h-15a2 2 0 01-2-2v-6l3-7z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
);

/* Search tunables */
const SEARCH_DEBOUNCE_MS  = 700;
const SEARCH_PREVIEW_SIZE = 8;

const buildSearchQuery = (term) => ({
    per_page: SEARCH_PREVIEW_SIZE,
    page:     1,
    name:     term,
});

/* ─────────────────────────────────────────────────────────────
   VENDOR NAVIGATION — single source of truth
   "Vendor" in the UI === "Brand" in the backend (GET_VENDORS).
   ───────────────────────────────────────────────────────────── */
const useGoToVendor = () => {
    const navigate = useNavigate();
    return useCallback(
        (vendor) => {
            if (!vendor) return;
            /* Accepts either a raw backend vendor or a normalized vendor.
               buildVendorUrl reads contact_id via getVendorId. */
            navigate(buildVendorUrl(vendor.raw ?? vendor));
        },
        [navigate]
    );
};

/* ─────────────────────────────────────────────────────────────
   SECTION CONFIG
   ───────────────────────────────────────────────────────────── */
const SECTIONS = [
    { key: 'new_arrivals', label: 'New Arrivals',         subtitle: 'Fresh stock, just landed',     icon: SparkleIcon, accent: NAVY,      lightBg: CYAN,      badgeText: 'NEW' },
    { key: 'best_sellers', label: 'Best Sellers',         subtitle: 'Top picks from other agents',  icon: TrophyIcon,  accent: '#C07B2A', lightBg: '#FDF5E9', badgeText: 'HOT' },
    { key: 'preorder',     label: 'Early Access Orders',  subtitle: 'Reserve before stock runs out', icon: ClockIcon,   accent: '#5B4FCF', lightBg: '#F0EEFF', badgeText: 'EARLY ACCESS' },
    { key: 'clearance',    label: 'Clearance Sales',      subtitle: 'Deep discounts, while stocks last', icon: TagIcon, accent: '#C53030', lightBg: '#FFF0F0', badgeText: 'SALE' },
];

const ITEMS_PER_SECTION = 6;
const SKELETON_KEYS     = Array.from({ length: ITEMS_PER_SECTION }, (_, i) => i);
const NEW_ARRIVALS      = SECTIONS[0];
const TRAILING_SECTIONS = SECTIONS.slice(1);

const SHOP_BY_VENDOR_COUNT = 8;  /* horizontal strip */
const TOP_VENDORS_COUNT    = 6;  /* mobile grid (3×2) */

/* ─────────────────────────────────────────────────────────────
   SHARED PRIMITIVES
   ───────────────────────────────────────────────────────────── */

const Card = memo(function Card({ children, p, ...rest }) {
    return (
        <Box
            bg={SURFACE}
            borderRadius="12px"
            p={p ?? '12px'}
            border="1px solid"
            borderColor={BORDER}
            boxShadow={SHADOW_SM}
            {...rest}
        >
            {children}
        </Box>
    );
});

const SectionTitle = memo(function SectionTitle({
    title, subtitle, icon: Icon, accent = NAVY, lightBg, badge, onSeeAll,
}) {
    return (
        <Flex align="center" justify="space-between" mb="10px" gap="8px">
            <Flex align="center" gap="8px" minW="0" flex="1">
                {Icon && (
                    <Flex
                        align="center"
                        justify="center"
                        bg={accent}
                        color="white"
                        borderRadius="7px"
                        w="24px"
                        h="24px"
                        flexShrink={0}
                    >
                        <Icon />
                    </Flex>
                )}
                <Box minW="0" flex="1">
                    <Flex align="center" gap="6px" flexWrap="wrap">
                        <Heading
                            fontSize="14px"
                            fontWeight="800"
                            color={INK}
                            lineHeight="1.2"
                            fontFamily={FONT}
                            letterSpacing="-0.01em"
                            noOfLines={1}
                        >
                            {title}
                        </Heading>
                        {badge && (
                            <Badge
                                bg={`${accent}15`}
                                color={accent}
                                fontSize="8px"
                                fontWeight="800"
                                px="5px"
                                py="1.5px"
                                borderRadius="4px"
                                letterSpacing="0.06em"
                                border={`1px solid ${accent}30`}
                            >
                                {badge}
                            </Badge>
                        )}
                    </Flex>
                    {subtitle && (
                        <Text fontSize="10.5px" color={FAINT} mt="1px" fontWeight="500" noOfLines={1}>
                            {subtitle}
                        </Text>
                    )}
                </Box>
            </Flex>
            {onSeeAll && (
                <Button
                    variant="ghost"
                    size="xs"
                    color={accent}
                    fontWeight="600"
                    fontSize="10.5px"
                    px="6px"
                    h="24px"
                    rightIcon={<ChevronRightIcon />}
                    _hover={{ bg: lightBg ?? SURFACE_ALT }}
                    _active={{ bg: lightBg ?? SURFACE_ALT }}
                    borderRadius="6px"
                    onClick={onSeeAll}
                    flexShrink={0}
                >
                    See all
                </Button>
            )}
        </Flex>
    );
});

const ProductSkeleton = memo(function ProductSkeleton() {
    return (
        <Box borderRadius="8px" overflow="hidden" bg={SURFACE} border="1px solid" borderColor={BORDER}>
            <Skeleton h="140px" w="full" startColor={SURFACE_ALT} endColor={BORDER} />
            <Box p="8px">
                <SkeletonText noOfLines={2} spacing="1.5" skeletonHeight="2" startColor={SURFACE_ALT} endColor={BORDER} />
                <Skeleton mt="2" h="4" w="50%" borderRadius="3px" startColor={SURFACE_ALT} endColor={BORDER} />
            </Box>
        </Box>
    );
});

const EmptySection = memo(function EmptySection({ label, lightBg, icon: Icon, onBrowse }) {
    return (
        <Flex
            direction="column"
            align="center"
            justify="center"
            bg={lightBg}
            borderRadius="10px"
            p="22px 18px"
            textAlign="center"
            gridColumn="1 / -1"
            border="1px dashed"
            borderColor={BORDER}
        >
            <Flex w="32px" h="32px" borderRadius="8px" bg={SURFACE} align="center" justify="center" mb="8px" color={MUTED} boxShadow={SHADOW_SM}>
                {Icon && <Icon />}
            </Flex>
            <Text fontSize="12px" fontWeight="600" color={INK} mb="1px">No {label.toLowerCase()} yet</Text>
            <Text fontSize="10.5px" color={FAINT} mb="8px">Check back soon — fresh stock arrives daily.</Text>
            {onBrowse && (
                <Button size="xs" variant="outline" borderColor={BORDER} color={NAVY} onClick={onBrowse} fontSize="10.5px" h="26px">
                    Browse all
                </Button>
            )}
        </Flex>
    );
});

/* ─────────────────────────────────────────────────────────────
   SEARCH — sticky pill on dashboard, full-screen modal on tap
   ───────────────────────────────────────────────────────────── */

const formatPrice = (item) => {
    const raw =
        item?.rate ??
        item?.price ??
        item?.selling_price ??
        item?.cf_obana_price ??
        null;
    if (raw == null) return '';
    const n = Number(raw);
    if (!Number.isFinite(n)) return '';
    return `₦${n.toLocaleString()}`;
};

const StickySearchTrigger = memo(function StickySearchTrigger({ onClick }) {
    return (
        <Box
            position="sticky"
            top="0"
            zIndex={5}
            bg={SURFACE_ALT}
            pt="2px"
            pb="10px"
            mx="-10px"
            px="10px"
        >
            <Flex
                as="button"
                type="button"
                onClick={onClick}
                align="center"
                gap="10px"
                w="full"
                h="42px"
                px="14px"
                bg={SURFACE}
                border="1px solid"
                borderColor={BORDER}
                borderRadius="22px"
                color={FAINT}
                fontSize="13px"
                fontFamily={FONT}
                cursor="pointer"
                transition="border-color 0.15s ease, background 0.15s ease"
                _hover={{ borderColor: NAVY }}
                _active={{ bg: SURFACE_ALT }}
                aria-label="Open search"
                textAlign="left"
            >
                <Box color={FAINT} flexShrink={0}>
                    <SearchIcon />
                </Box>
                <Text as="span" flex="1" minW="0" noOfLines={1}>
                    Search products, brands, vendors…
                </Text>
            </Flex>
        </Box>
    );
});

const SearchPreviewItem = memo(function SearchPreviewItem({ item, onClick }) {
    const image = item?.image_url ?? item?.image ?? item?.image_name ?? null;
    const name  = item?.name ?? item?.item_name ?? 'Unnamed product';
    const sku   = item?.sku ?? item?.item_id ?? '';
    const price = formatPrice(item);

    return (
        <Flex
            as="button"
            type="button"
            onClick={() => onClick(item)}
            align="center"
            gap="12px"
            w="full"
            p="12px 16px"
            bg="transparent"
            _hover={{ bg: SURFACE_ALT }}
            _active={{ bg: BORDER }}
            transition="background 0.12s ease"
            borderBottom="1px solid"
            borderColor={BORDER}
            textAlign="left"
            cursor="pointer"
        >
            <Box
                w="44px"
                h="44px"
                borderRadius="8px"
                bg={SURFACE_ALT}
                border="1px solid"
                borderColor={BORDER}
                flexShrink={0}
                overflow="hidden"
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                {image ? (
                    <Image src={image} alt="" w="full" h="full" objectFit="cover" loading="lazy" />
                ) : (
                    <Box color={FAINT}><SearchIcon /></Box>
                )}
            </Box>
            <Box flex="1" minW="0">
                <Text fontSize="13px" fontWeight="600" color={INK} noOfLines={1} fontFamily={FONT}>
                    {name}
                </Text>
                {(sku || price) && (
                    <Flex gap="8px" mt="2px" align="center">
                        {price && (
                            <Text fontSize="11px" color={NAVY} fontWeight="700">
                                {price}
                            </Text>
                        )}
                        {sku && (
                            <Text fontSize="10px" color={FAINT} noOfLines={1}>
                                {sku}
                            </Text>
                        )}
                    </Flex>
                )}
            </Box>
        </Flex>
    );
});

const SearchPreviewSkeleton = memo(function SearchPreviewSkeleton() {
    return (
        <Flex align="center" gap="12px" p="12px 16px" borderBottom="1px solid" borderColor={BORDER}>
            <Skeleton w="44px" h="44px" borderRadius="8px" startColor={SURFACE_ALT} endColor={BORDER} />
            <Box flex="1">
                <Skeleton h="3" w="80%" borderRadius="3px" mb="2" startColor={SURFACE_ALT} endColor={BORDER} />
                <Skeleton h="2.5" w="40%" borderRadius="3px" startColor={SURFACE_ALT} endColor={BORDER} />
            </Box>
        </Flex>
    );
});

const MobileSearchModal = memo(function MobileSearchModal({
    isOpen, onClose, value, onChange, items, isLoading, onItemClick, onSeeAll,
}) {
    const inputRef = useRef(null);
    const trimmed = (value ?? '').trim();
    const hasQuery   = trimmed.length > 0;
    const hasResults = items?.length > 0;

    const handleSubmit = useCallback(
        (e) => {
            e.preventDefault();
            if (!hasQuery) return;
            onSeeAll();
        },
        [hasQuery, onSeeAll]
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="full"
            motionPreset="slideInBottom"
            initialFocusRef={inputRef}
        >
            <ModalOverlay bg="blackAlpha.600" />
            <ModalContent borderRadius="0" m="0" maxH="100vh" bg={SURFACE} fontFamily={FONT}>
                <ModalBody p="0" display="flex" flexDirection="column">
                    <Box p="12px 14px" borderBottom="1px solid" borderColor={BORDER} bg={SURFACE}>
                        <form onSubmit={handleSubmit}>
                            <Flex align="center" gap="10px">
                                <InputGroup flex="1">
                                    <InputLeftElement pointerEvents="none" h="42px">
                                        <Box color={FAINT}><SearchIcon /></Box>
                                    </InputLeftElement>
                                    <Input
                                        ref={inputRef}
                                        type="search"
                                        placeholder="Search products, brands…"
                                        value={value}
                                        onChange={(e) => onChange(e.target.value)}
                                        bg={SURFACE_ALT}
                                        border="1px solid"
                                        borderColor={BORDER}
                                        h="42px"
                                        pl="42px"
                                        pr={hasQuery ? '38px' : '14px'}
                                        fontSize="16px"
                                        borderRadius="10px"
                                        _focus={{ borderColor: NAVY, bg: SURFACE, boxShadow: `0 0 0 3px ${CYAN}` }}
                                        fontFamily={FONT}
                                        autoComplete="off"
                                    />
                                    {hasQuery && (
                                        <Box
                                            position="absolute"
                                            right="6px"
                                            top="50%"
                                            transform="translateY(-50%)"
                                            zIndex={2}
                                        >
                                            <IconButton
                                                aria-label="Clear search"
                                                icon={<CloseIcon />}
                                                onClick={() => onChange('')}
                                                variant="ghost"
                                                size="xs"
                                                h="28px"
                                                minW="28px"
                                                color={FAINT}
                                                _hover={{ color: INK, bg: 'transparent' }}
                                            />
                                        </Box>
                                    )}
                                </InputGroup>
                                <Text
                                    as="button"
                                    type="button"
                                    fontSize="13px"
                                    fontWeight="700"
                                    color={NAVY}
                                    onClick={onClose}
                                    px="4px"
                                    flexShrink={0}
                                    aria-label="Close search"
                                >
                                    Cancel
                                </Text>
                            </Flex>
                        </form>
                    </Box>

                    <Box flex="1" overflowY="auto">
                        {!hasQuery ? (
                            <Flex direction="column" align="center" justify="center" p="60px 20px" gap="10px">
                                <Box color={FAINT}><SearchIcon /></Box>
                                <Text fontSize="13px" color={FAINT} textAlign="center">
                                    Start typing to search products
                                </Text>
                            </Flex>
                        ) : isLoading ? (
                            <>
                                {Array.from({ length: 5 }).map((_, i) => <SearchPreviewSkeleton key={i} />)}
                            </>
                        ) : !hasResults ? (
                            <Flex direction="column" align="center" justify="center" p="40px 20px" gap="10px">
                                <Box color={FAINT}><InboxIcon /></Box>
                                <Text fontSize="13px" fontWeight="700" color={INK}>
                                    No products found
                                </Text>
                                <Text fontSize="11px" color={FAINT} textAlign="center" maxW="240px">
                                    Try a different keyword, or browse all products.
                                </Text>
                                <Button
                                    mt="6px"
                                    size="sm"
                                    bg={NAVY}
                                    color="white"
                                    _hover={{ bg: NAVY_DEEP }}
                                    borderRadius="8px"
                                    fontSize="11px"
                                    onClick={onSeeAll}
                                >
                                    Browse all products
                                </Button>
                            </Flex>
                        ) : (
                            <>
                                <Box p="10px 16px 4px" bg={SURFACE_ALT}>
                                    <Text fontSize="10px" color={FAINT} fontWeight="700" letterSpacing="0.1em">
                                        TOP RESULTS
                                    </Text>
                                </Box>
                                {items.slice(0, SEARCH_PREVIEW_SIZE).map((item, i) => (
                                    <SearchPreviewItem
                                        key={item.item_id ?? i}
                                        item={item}
                                        onClick={onItemClick}
                                    />
                                ))}
                                <Flex
                                    as="button"
                                    type="button"
                                    onClick={onSeeAll}
                                    p="14px 16px"
                                    justify="space-between"
                                    align="center"
                                    borderTop="1px solid"
                                    borderColor={BORDER}
                                    cursor="pointer"
                                    _hover={{ bg: SURFACE_ALT }}
                                    _active={{ bg: BORDER }}
                                    w="full"
                                    bg={SURFACE}
                                >
                                    <Text fontSize="13px" color={NAVY} fontWeight="700">
                                        See all results for "{trimmed}"
                                    </Text>
                                    <Box color={NAVY}><ArrowRightIcon /></Box>
                                </Flex>
                            </>
                        )}
                    </Box>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
});

/* ─────────────────────────────────────────────────────────────
   GREETING BANNER
   ───────────────────────────────────────────────────────────── */
const GREETING_WORDS = {
    morning:   { word: 'Good morning',   emoji: '☀️' },
    afternoon: { word: 'Good afternoon', emoji: '🌤️' },
    evening:   { word: 'Good evening',   emoji: '🌙' },
};

const getTimeOfDay = () => {
    const h = new Date().getHours();
    if (h < 12) return 'morning';
    if (h < 17) return 'afternoon';
    return 'evening';
};

const GreetingBanner = memo(function GreetingBanner({ name, navigate }) {
    const tod = useMemo(getTimeOfDay, []);
    const { word, emoji } = GREETING_WORDS[tod];
    return (
        <Flex align="center" justify="space-between" mb="14px" gap="10px">
            <Box minW="0" flex="1">
                <Flex align="center" gap="6px" mb="1px">
                    <Box fontSize="14px" lineHeight="1">{emoji}</Box>
                    <Text
                        fontSize="10px"
                        color={FAINT}
                        fontWeight="600"
                        letterSpacing="0.06em"
                        textTransform="uppercase"
                        noOfLines={1}
                    >
                        {word}
                    </Text>
                </Flex>
                <Heading
                    fontSize="16px"
                    color={INK}
                    fontWeight="800"
                    fontFamily={FONT}
                    letterSpacing="-0.02em"
                    lineHeight="1.2"
                    textTransform="capitalize"
                    noOfLines={1}
                >
                    Welcome back, {name}
                </Heading>
            </Box>
            <Button
                onClick={() => navigate('/dashboard/products')}
                bg={NAVY}
                color="white"
                _hover={{ bg: NAVY_DEEP }}
                _active={{ bg: NAVY_DEEP }}
                rightIcon={<ChevronRightIcon />}
                borderRadius="8px"
                h="34px"
                px="10px"
                fontSize="11px"
                fontWeight="700"
                fontFamily={FONT}
                flexShrink={0}
            >
                Browse
            </Button>
        </Flex>
    );
});

/* ─────────────────────────────────────────────────────────────
   VENDOR AVATAR
   ───────────────────────────────────────────────────────────── */

const VendorAvatar = memo(function VendorAvatar({ vendor, size, fontSize }) {
    const initial = (vendor.name?.[0] ?? '?').toUpperCase();
    return (
        <Flex
            w={size}
            h={size}
            borderRadius="full"
            bg={vendor.image ? SURFACE : CYAN}
            color={NAVY}
            align="center"
            justify="center"
            fontWeight="800"
            fontSize={fontSize}
            fontFamily={FONT}
            overflow="hidden"
            border="1px solid"
            borderColor={BORDER}
            flexShrink={0}
        >
            {vendor.image ? (
                <Image
                    src={vendor.image}
                    alt={vendor.name}
                    w="full"
                    h="full"
                    objectFit="cover"
                    fallback={
                        <Flex w="full" h="full" align="center" justify="center" bg={CYAN}>
                            {initial}
                        </Flex>
                    }
                />
            ) : (
                initial
            )}
        </Flex>
    );
});

/* ─────────────────────────────────────────────────────────────
   SHOP BY VENDOR — horizontal scrolling strip
   ───────────────────────────────────────────────────────────── */

const ShopByVendorSkeleton = memo(function ShopByVendorSkeleton() {
    return (
        <Flex
            direction="column"
            align="center"
            gap="6px"
            flexShrink={0}
            minW="72px"
            p="6px"
        >
            <SkeletonCircle size="52px" startColor={SURFACE_ALT} endColor={BORDER} />
            <Skeleton h="2.5" w="60%" borderRadius="3px" startColor={SURFACE_ALT} endColor={BORDER} />
        </Flex>
    );
});

const ShopByVendor = memo(function ShopByVendor({ navigate }) {
    const { vendors: rawVendors, isLoading } = useVendors({ per_page: 30});
    const vendors = useMemo(
        () => rawVendors.filter(isDisplayableVendor).map(normalizeVendor),
        [rawVendors]
    );
    const goToVendor = useGoToVendor();

    const displayVendors = vendors.slice(0, SHOP_BY_VENDOR_COUNT);

    return (
        <Card mb="14px">
            <SectionTitle
                title="Shop by Vendor"
                subtitle="Browse products from verified sellers"
                icon={StoreIcon}
                accent={NAVY}
                lightBg={CYAN}
                onSeeAll={() => navigate('/dashboard/vendors')}
            />
            <Flex
                gap="10px"
                overflowX="auto"
                pb="4px"
                mx="-4px"
                px="4px"
                sx={{
                    WebkitOverflowScrolling: 'touch',
                    scrollSnapType: 'x mandatory',
                }}
                css={{
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                }}
            >
                {isLoading
                    ? Array.from({ length: SHOP_BY_VENDOR_COUNT }).map((_, i) => (
                        <ShopByVendorSkeleton key={i} />
                    ))
                    : displayVendors.length === 0
                    ? (
                        <Flex
                            w="full"
                            justify="center"
                            align="center"
                            py="16px"
                            color={FAINT}
                            fontSize="11px"
                        >
                            No vendors available right now
                        </Flex>
                    )
                    : displayVendors.map((v) => (
                        <Flex
                            key={v.id}
                            direction="column"
                            align="center"
                            gap="6px"
                            flexShrink={0}
                            minW="72px"
                            cursor="pointer"
                            onClick={() => goToVendor(v)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    goToVendor(v);
                                }
                            }}
                            p="6px"
                            borderRadius="8px"
                            _hover={{ bg: SURFACE_ALT }}
                            _active={{ bg: SURFACE_ALT }}
                            role="button"
                            tabIndex={0}
                            aria-label={`Shop ${v.name}`}
                            sx={{ scrollSnapAlign: 'start' }}
                        >
                            <VendorAvatar vendor={v} size="52px" fontSize="18px" />
                            <Text
                                fontSize="10.5px"
                                fontWeight="600"
                                color={INK}
                                noOfLines={1}
                                maxW="72px"
                                textAlign="center"
                            >
                                {v.name}
                            </Text>
                        </Flex>
                    ))}
            </Flex>
        </Card>
    );
});

/* ─────────────────────────────────────────────────────────────
   TOP VENDORS — 3-col grid
   ───────────────────────────────────────────────────────────── */

const VendorCard = memo(function VendorCard({ vendor, onClick }) {
    return (
        <Flex
            direction="column"
            align="center"
            gap="6px"
            p="8px 6px"
            borderRadius="10px"
            cursor="pointer"
            onClick={onClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick?.();
                }
            }}
            border="1px solid"
            borderColor={BORDER}
            bg={SURFACE}
            transition="all 0.15s ease"
            _hover={{ borderColor: NAVY, boxShadow: SHADOW_SM }}
            _active={{ borderColor: NAVY }}
            role="button"
            tabIndex={0}
            aria-label={`View ${vendor.name}`}
        >
            <VendorAvatar vendor={vendor} size="42px" fontSize="14px" />
            <Box textAlign="center" minW="0" w="full">
                <Text fontSize="10.5px" fontWeight="700" color={INK} noOfLines={1}>{vendor.name}</Text>
                {vendor.productCount != null && (
                    <Text fontSize="9.5px" color={FAINT}>{vendor.productCount} items</Text>
                )}
            </Box>
        </Flex>
    );
});

const VendorSkeleton = memo(function VendorSkeleton() {
    return (
        <Box p="8px 6px" borderRadius="10px" border="1px solid" borderColor={BORDER}>
            <Flex direction="column" align="center" gap="6px">
                <SkeletonCircle size="42px" startColor={SURFACE_ALT} endColor={BORDER} />
                <Skeleton h="2.5" w="80%" borderRadius="3px" startColor={SURFACE_ALT} endColor={BORDER} />
                <Skeleton h="2" w="50%" borderRadius="3px" startColor={SURFACE_ALT} endColor={BORDER} />
            </Flex>
        </Box>
    );
});

const TopVendors = memo(function TopVendors({ navigate }) {
    const { vendors: rawVendors, isLoading } = useVendors({ per_page: 30 });
    const vendors = useMemo(
        () => rawVendors.filter(isDisplayableVendor).map(normalizeVendor),
        [rawVendors]
    );
    const goToVendor = useGoToVendor();

    /* Skip the ones already shown in ShopByVendor strip, take next 6.
       If total <= 8, just show all (max 6) since there's no overlap to skip. */
    const displayVendors = useMemo(() => {
        if (vendors.length <= SHOP_BY_VENDOR_COUNT) return vendors.slice(0, TOP_VENDORS_COUNT);
        return vendors.slice(SHOP_BY_VENDOR_COUNT, SHOP_BY_VENDOR_COUNT + TOP_VENDORS_COUNT);
    }, [vendors]);

    return (
        <Card mb="14px">
            <SectionTitle
                title="Top Vendors"
                subtitle="More verified sellers to explore"
                icon={StoreIcon}
                accent={NAVY}
                lightBg={CYAN}
                onSeeAll={() => navigate('/dashboard/vendors')}
            />
            {isLoading ? (
                <SimpleGrid columns={4} spacing="8px">
                    {Array.from({ length: 4 }).map((_, i) => <VendorSkeleton key={i} />)}
                </SimpleGrid>
            ) : displayVendors.length === 0 ? (
                <Flex
                    direction="column"
                    align="center"
                    justify="center"
                    bg={SURFACE_ALT}
                    borderRadius="10px"
                    p="20px 16px"
                    textAlign="center"
                    border="1px dashed"
                    borderColor={BORDER}
                >
                    <Flex w="32px" h="32px" borderRadius="8px" bg={SURFACE} align="center" justify="center" mb="8px" color={MUTED} boxShadow={SHADOW_SM}>
                        <StoreIcon />
                    </Flex>
                    <Text fontSize="12px" fontWeight="600" color={INK} mb="1px">
                        That's all the vendors for now
                    </Text>
                    <Text fontSize="10.5px" color={FAINT}>
                        Scroll up to see featured sellers above.
                    </Text>
                </Flex>
            ) : (
                <SimpleGrid columns={4} spacing="8px">
                    {displayVendors.slice(0, 4).map((v) => (
                        <VendorCard
                            key={v.id}
                            vendor={v}
                            onClick={() => goToVendor(v)}
                        />
                    ))}
                </SimpleGrid>
            )}
        </Card>
    );
});

/* ─────────────────────────────────────────────────────────────
   PRODUCT SECTION
   ───────────────────────────────────────────────────────────── */
const ProductSection = memo(function ProductSection({ section, navigate }) {
    const { data, isLoading } = useGetProducts({
        filter: section.key,
        per_page: ITEMS_PER_SECTION,
    });

    const items   = data?.items?.slice(0, ITEMS_PER_SECTION) ?? [];
    const onSeeAll = () => navigate(`/dashboard/products?filter=${section.key}`);
    const onBrowse = () => navigate('/dashboard/products');

    return (
        <Card mb="14px">
            <SectionTitle
                title={section.label}
                subtitle={section.subtitle}
                icon={section.icon}
                accent={section.accent}
                lightBg={section.lightBg}
                badge={section.badgeText}
                onSeeAll={onSeeAll}
            />
            {isLoading ? (
                <SimpleGrid columns={2} spacing="8px">
                    {SKELETON_KEYS.map((i) => <ProductSkeleton key={i} />)}
                </SimpleGrid>
            ) : items.length === 0 ? (
                <EmptySection
                    label={section.label}
                    lightBg={section.lightBg}
                    icon={section.icon}
                    onBrowse={onBrowse}
                />
            ) : (
                <SimpleGrid columns={2} spacing="8px">
                    {items.map((item, i) => (
                        <Box
                            key={item.item_id ?? i}
                            position="relative"
                            minW="0"
                            w="full"
                            overflow="hidden"
                        >
                            <ProductItemNew item={item} />
                            {item._backfill && (
                                <Badge
                                    position="absolute"
                                    top="5px"
                                    left="5px"
                                    bg="whiteAlpha.900"
                                    color={MUTED}
                                    fontSize="8.5px"
                                    fontWeight="700"
                                    px="4px"
                                    py="1.5px"
                                    borderRadius="4px"
                                    pointerEvents="none"
                                    boxShadow={SHADOW_SM}
                                >
                                    Related
                                </Badge>
                            )}
                        </Box>
                    ))}
                </SimpleGrid>
            )}
        </Card>
    );
});

/* ─────────────────────────────────────────────────────────────
   MAIN
   ───────────────────────────────────────────────────────────── */
const MobileDashboard = () => {
    const navigate = useNavigate();
    const { flattenedUser: details } = useGetUserDetails();


    
    /* ── Search state ───────────────────────────────────────── */
    const searchModal = useDisclosure();
    const [searchValue, setSearchValue] = useState('');
    const debouncedQuery = useDebounce(searchValue, SEARCH_DEBOUNCE_MS);
    const trimmedDebounced = useMemo(() => debouncedQuery.trim(), [debouncedQuery]);

    const { products, loading: productsLoading } = useSelector(
        (state) => state.productReducer
    );
    const previewItems = useMemo(
        () => (products?.items ?? []).slice(0, SEARCH_PREVIEW_SIZE),
        [products]
    );
    const isSearchingActive =
        productsLoading || trimmedDebounced !== searchValue.trim();

    

    useEffect(() => {
        if (!searchModal.isOpen) {
            setSearchValue('');
        }
    }, [searchModal.isOpen]);

    const handleProductClick = useCallback((item) => {
    const itemId = item?.item_id ?? item?.id;
    searchModal.onClose();
    if (itemId) {
        navigate(`/dashboard/product/details/${itemId}`);
    } else {
        /* No id on item — fall back to listing with search term */
        const q = searchValue.trim();
        navigate(q ? `/dashboard/products?q=${encodeURIComponent(q)}` : '/dashboard/products');
    }
}, [navigate, searchModal, searchValue]);

    const handleNavigateToResults = useCallback(() => {
        const q = searchValue.trim();
        searchModal.onClose();
        if (q) {
            navigate(`/dashboard/products?q=${encodeURIComponent(q)}`);
        } else {
            navigate('/dashboard/products');
        }
    }, [searchValue, navigate, searchModal]);

    const displayName = useMemo(
        () =>
            details?.attributes?.first_name ||
            details?.first_name ||
            details?.email?.split('@')[0] ||
            'there',
        [details]
    );

    return (
        <Box
            bg={SURFACE_ALT}
            minH="100vh"
            pb="96px"
            fontFamily={FONT}
            px="10px"
            pt="14px"
            w="100%"
            maxW="100vw"
            overflowX="hidden"
        >
            <StickySearchTrigger onClick={searchModal.onOpen} />
            <GreetingBanner name={displayName} navigate={navigate} />
            <ShopByVendor navigate={navigate} />
            <ProductSection section={NEW_ARRIVALS} navigate={navigate} />
            <TopVendors navigate={navigate} />
            {TRAILING_SECTIONS.map((section) => (
                <ProductSection key={section.key} section={section} navigate={navigate} />
            ))}
            <Brands />

            <MobileSearchModal
                isOpen={searchModal.isOpen}
                onClose={searchModal.onClose}
                value={searchValue}
                onChange={setSearchValue}
                items={previewItems}
                isLoading={isSearchingActive}
                onItemClick={handleProductClick}
                onSeeAll={handleNavigateToResults}
            />
        </Box>
    );
};

export default MobileDashboard;